import { IDropdownListItem, IDropdownSelect } from "types/gaiaComponents/dropdownButton";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { ChevronDownIcon, ChevronUpIcon, CircleXIcon } from "lucide-react";
import { DropdownList } from "components/common/dropdown/DropdownList.component";
import { ICON_SIZE, KEY } from "components/common/constants";
import { DropdownSelectTypes } from "types/gaiaComponents/dropdownButton.types";

export const DropdownSelect = (props: IDropdownSelect) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const setOpen = (open: boolean) => {
        setDropdownOpen(open);
        if (props.isSearchable) {
            if (open) {
                // Defer focus to ensure the input is mounted before focusing.
                requestAnimationFrame(() => searchInputRef.current?.focus());
            } else {
                setSearchTerm('');
            }
        }
        props.onOpenChange?.(open);
    };

    const filteredItems = useMemo<IDropdownListItem[]>(() => {
        if (!props.isSearchable) return props.items;

        const term = searchTerm.trim().toLowerCase();
        const filtered = term
            ? props.items.filter(item => item.text.toLowerCase().includes(term))
            : props.items;

        if (filtered.length === 0 && props.items.length > 0) {
            return [{
                key: '__no_match__',
                text: props.noMatchText ?? '',
                title: props.noMatchText,
                disabled: true,
                onClick: () => undefined,
            }];
        }

        return filtered;
    }, [props.isSearchable, props.items, props.noMatchText, searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === KEY.ESCAPE) {
                setOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [dropdownOpen]);

    // Events from the search header must not toggle the dropdown (e.g. typing Space, or clicking the input).
    const isFromHeader = (target: EventTarget | null) => !!headerRef.current?.contains(target as Node);

    const onClickDropdown = function (e: React.MouseEvent<HTMLDivElement>) {
        if (!props.disabled && !isFromHeader(e.target)) {
            setOpen(!dropdownOpen);
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if ((e.key === KEY.ENTER || e.key === KEY.SPACE) && !props.disabled && !isFromHeader(e.target)) {
            e.preventDefault();
            setOpen(!dropdownOpen);
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === KEY.ARROW_DOWN) {
            const firstOption = dropdownRef.current
                ?.querySelector<HTMLButtonElement>('[role="option"]:not([disabled])');
            if (firstOption) {
                e.preventDefault();
                firstOption.focus();
            }
        }
    };

    const searchInput = (
        <input
            ref={searchInputRef}
            type="text"
            className="ga-select__input ga-select__search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={props.searchPlaceholder}
            aria-label={props.searchAriaLabel}
        />
    );

    const header = props.isSearchable ? searchInput : props.header;

    return (
        <div ref={dropdownRef}
             className={`ga-native-select ga-select ${dropdownOpen? "ga-select--expanded":""} ${props.isEmpty? "ga-select--empty":""} ${props.isInvalid? "ga-select--invalid":""} ${props.disabled? "ga-select--disabled":""} ${props.isMulti? "ga-select--multi":""} ${props.extraClasses? props.extraClasses : ""}`}
             id={props.id}
             role="combobox"
             aria-haspopup="listbox"
             aria-expanded={dropdownOpen}
             aria-disabled={props.disabled}
             onClick={onClickDropdown}
             tabIndex={0}
             onKeyDown={handleKeyDown}>
            <div className="ga-select__main-area">
                {props.type === DropdownSelectTypes.VALUE &&
                    <div className="ga-select__value">{props.value}</div>
                }
                {props.type === DropdownSelectTypes.PLACEHOLDER &&
                    <div className="ga-select__placeholder">{props.placeholder}</div>
                }
                {props.type === DropdownSelectTypes.INPUT &&
                    <div className="ga-select__input">{props.input}</div>
                }
            </div>
            {dropdownOpen && !props.isEmpty && header &&
                <div className="ga-select__header" ref={headerRef}>
                    {header}
                </div>
            }
            {dropdownOpen && !props.isEmpty &&
                <DropdownList items={filteredItems}
                              position={props.position}
                              autoFocus={!header}/>
            }
            <div className="ga-select__suffix">
                { dropdownOpen ?
                    <ChevronUpIcon className="ga-select__action-icon" size={ICON_SIZE.MEDIUM} /> :
                    <ChevronDownIcon className="ga-select__action-icon" size={ICON_SIZE.MEDIUM} />
                }
                {props.isClearable &&
                    <CircleXIcon size={ICON_SIZE.SMALL} className="ga-icon ga-select__action-icon" />
                }
            </div>
        </div>
    );
}